import { useCallback, useEffect, useState } from 'react';
import { loadHome } from '../../app/home';
import type { TeamCard } from '../../app/home';
import { log } from '../../app/logger';

/**
 * B spec §2.4 — the Opponents tab's controller (build unit B-6): the team cards from the
 * engine's existing `loadHome` read (§3 map row 1 — the FILE facade, reused as-is).
 *
 * `loadHome` also returns A's pending/in-progress distillation queues; B renders only the
 * `teams` half — B's needs-finishing story lives on the Matches tab (`homeB.loadHomeB`), and
 * presenting a facade's result partially is using the engine, not altering it.
 *
 * Same `useHomeB` shape: one read on mount, `refresh` for return paths, completed-reads
 * counter rendered as `data-read`.
 */

const MODULE = 'ui-b/opponents';

type Phase = 'loading' | 'ready' | 'failed';

export interface OpponentsState {
  readonly phase: Phase;
  readonly teams: readonly TeamCard[];
  readonly reads: number;
}

export function useOpponents() {
  const [phase, setPhase] = useState<Phase>('loading');
  const [teams, setTeams] = useState<readonly TeamCard[]>([]);
  const [reads, setReads] = useState(0);

  const refresh = useCallback((): void => {
    void (async () => {
      const view = await loadHome();
      setTeams(view.teams);
      setPhase('ready');
      setReads((count) => count + 1);
    })().catch((cause: unknown) => {
      log.error(MODULE, 'B §2.4: the opponents list could not be read', {
        message: cause instanceof Error ? cause.message : String(cause),
      });
      setPhase('failed');
    });
  }, []);

  useEffect(refresh, [refresh]);

  return {
    state: { phase, teams, reads } satisfies OpponentsState,
    actions: { refresh },
  };
}
